import http from './api'
import type { APIPaginatedResponse, APIResponse, Filters, Pagination } from './types'
import { getRequestParamString } from './utils/utils'

const basePath = import.meta.env.VITE_API_PATH || import.meta.env.VITE_API_BASE_URL || ''
const apiBase = basePath ? (basePath.endsWith('/') ? basePath.slice(0, -1) : basePath) : ''

export type StockTakeItemApi = {
  id: number
  reference: string
  inventory_location_id: number
  inventory_location_name?: string
  status: string
  notes?: string | null
  created_by?: { uuid: string; name: string } | null
  created_at?: string
  updated_at?: string
  uuid?: string
  status_label?: string
  items_count?: number
}

export type StockTakeImportItemReviewApi = {
  uuid: string
  item_uuid: string | null
  item_name: string | null
  tracking_type: number | null
  tracking_type_label: string | null
  system_quantity: number | null
  counted_quantity: number | null
  system_quantity_base: number | null
  counted_quantity_base: number | null
  variance_quantity_base: number | null
  unit_uuid: string | null
  base_unit_uuid: string | null
  unit_factor_to_base: number | null
  batch_uuid?: string | null
  batch_number?: string | null
  expiry_date?: string | null
  serial_uuid?: string | null
  serial_number?: string | null
  system_status?: number | null
  system_status_label?: string | null
  counted_status?: number | null
  serial_variance_type?: string | null
}

export type StockTakeReviewApi = {
  uuid: string
  status: number | null
  status_label: string | null
  date: string | null
  reference_number: string | null
  inventory_location_id: number | null
  inventory_location_name?: string | null
  business_id: number | null
  items: StockTakeImportItemReviewApi[]
  summary: {
    total_items: number
    items_counted: number
    items_with_variance: number
    total_positive_variance_base: number
    total_negative_variance_base: number
    missing_serials: Array<{ serial_uuid: string; serial_number: string; item_uuid: string; item_name: string }>
    unexpected_serials: Array<{ serial_uuid: string; serial_number: string; item_uuid: string; item_name: string }>
  }
  submitted_at?: string | null
  submitted_by?: { uuid: string; name: string } | null
  approved_at?: string | null
  approved_by?: { uuid: string; name: string } | null
  completed_at?: string | null
  completed_by?: { uuid: string; name: string } | null
  started_by?: { uuid: string; name: string } | null
}

export type CreateStockTakeInput = {
  inventory_location_id: number
  reference?: string
  notes?: string
}

async function getStockTakes(filters?: Partial<Filters>, pagination?: Partial<Pagination>) {
  const paramString = getRequestParamString(filters, pagination)
  return await http.get<APIPaginatedResponse<StockTakeItemApi>>(`${apiBase}/stock-takes${paramString}`)
}

async function createStockTake(input: CreateStockTakeInput) {
  return await http.post<APIResponse<StockTakeItemApi>>(`${apiBase}/stock-takes`, input)
}

async function downloadTemplate(inventoryLocationId: number) {
  return await http.get<Blob>(`${apiBase}/imports/templates/stock-take?inventory_location_id=${inventoryLocationId}`, {
    responseType: 'blob',
  })
}

async function importStockTake(
  file: File,
  date: string,
  inventoryLocationId: number,
  reference?: string,
  notes?: string,
) {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('date', date)
  formData.append('inventory_location_id', String(inventoryLocationId))
  if (reference) formData.append('reference_number', reference)
  if (notes) formData.append('notes', notes)
  return await http.post<APIResponse<StockTakeItemApi>>(`${apiBase}/inventory/stock-takes/import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    errorHandle: false,
  } as any)
}

async function getStockTakeReview(stockTakeUuid: string) {
  return await http.get<APIResponse<StockTakeReviewApi>>(`${apiBase}/inventory/stock-takes/${stockTakeUuid}/review`)
}

async function submitStockTake(stockTakeUuid: string) {
  return await http.post<
    APIResponse<{
      uuid: string
      status: number
      status_label: string
      submitted_at: string
      submitted_by: { uuid: string; name: string }
    }>
  >(`${apiBase}/inventory/stock-takes/${stockTakeUuid}/submit`, {})
}

async function approveStockTake(stockTakeUuid: string) {
  return await http.post<
    APIResponse<{
      uuid: string
      status: number
      status_label: string
      approved_at: string
      approved_by: { uuid: string; name: string }
    }>
  >(`${apiBase}/inventory/stock-takes/${stockTakeUuid}/approve`, {})
}

async function cancelStockTake(stockTakeUuid: string) {
  return await http.post<APIResponse<{ uuid: string; status: number; status_label: string }>>(
    `${apiBase}/inventory/stock-takes/${stockTakeUuid}/cancel`,
    {},
  )
}

// List review-ready stock takes (validated/submitted, ready for approval)
async function getReviewReadyStockTakes(filters?: Partial<Filters>, pagination?: Partial<Pagination>) {
  const paramString = getRequestParamString(filters, pagination)
  return await http.get<APIPaginatedResponse<StockTakeItemApi>>(
    `${apiBase}/inventory/stock-takes/review-ready${paramString}`,
  )
}

// List historical/completed stock takes
async function getStockTakeHistory(filters?: Partial<Filters>, pagination?: Partial<Pagination>) {
  const paramString = getRequestParamString(filters, pagination)
  return await http.get<APIPaginatedResponse<StockTakeItemApi>>(
    `${apiBase}/inventory/stock-takes/history${paramString}`,
  )
}

// Complete the approved stock take — applies inventory adjustments
async function completeStockTake(stockTakeUuid: string) {
  return await http.post<
    APIResponse<{
      uuid: string
      status: number
      status_label: string
      completed_at: string
      completed_by: { uuid: string; name: string }
    }>
  >(`${apiBase}/inventory/stock-takes/${stockTakeUuid}/complete`, {})
}

const stockTakesService = {
  getStockTakes,
  getReviewReadyStockTakes,
  getStockTakeHistory,
  createStockTake,
  downloadTemplate,
  importStockTake,
  getStockTakeReview,
  submitStockTake,
  approveStockTake,
  cancelStockTake,
  completeStockTake,
}

export default stockTakesService
